import { useState } from 'react';
import type { EmailSummary, EmailSummaryItem, EmailUrgency } from '../../../lib/emailApi';
import { ingestEmailInvoice } from '../../../lib/emailApi';

interface Props {
  summary: EmailSummary;
  userId: string;
}

const URGENCY_STYLE: Record<EmailUrgency, string> = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-gray-100 text-gray-600',
};

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('ro-RO', { hour: 'numeric', minute: '2-digit' });
}

type IngestState =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'done'; fileName: string }
  | { status: 'error'; message: string };

/** A single processed email row with its AI summary, context and actions. */
function EmailRow({ email, userId }: { email: EmailSummaryItem; userId: string }) {
  const [ingest, setIngest] = useState<IngestState>({ status: 'idle' });

  const handleIngest = async () => {
    const attachment = email.invoice?.attachment;
    if (!attachment) return;
    setIngest({ status: 'pending' });
    try {
      const staged = await ingestEmailInvoice(
        userId,
        email.gmailMessageId,
        attachment.attachmentId,
      );
      setIngest({ status: 'done', fileName: staged.fileName });
    } catch (err) {
      setIngest({
        status: 'error',
        message: err instanceof Error ? err.message : 'Factura nu a putut fi pregătită',
      });
    }
  };

  return (
    <li className="rounded-md border border-gray-200 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-medium text-gray-900">
            {email.isUnread && (
              <span
                className="mr-1.5 inline-block h-2 w-2 rounded-full bg-blue-500 align-middle"
                aria-label="Necitit"
              />
            )}
            {email.subject}
          </p>
          <p className="truncate text-xs text-gray-500">
            {email.fromName}
            {email.fromEmail && email.fromEmail !== email.fromName ? ` · ${email.fromEmail}` : ''}
            {' · '}
            {formatTime(email.receivedAt)}
          </p>
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${URGENCY_STYLE[email.urgency]}`}
        >
          {email.urgency}
        </span>
      </div>

      {email.summary && <p className="mt-2 text-sm text-gray-700">{email.summary}</p>}
      {email.context && (
        <p className="mt-1 text-xs text-gray-500">
          <span className="font-medium">Context:</span> {email.context}
        </p>
      )}

      {email.suggestedActions.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {email.suggestedActions.map((action, i) => (
            <li key={i} className="rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
              {action}
            </li>
          ))}
        </ul>
      )}

      {email.invoice && (
        <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-2.5">
          <p className="text-sm text-emerald-900">
            Hei, tocmai ai primit factura de la <strong>{email.invoice.vendor}</strong>. Vrei să o
            adăugăm în ingestie?
            {email.invoice.hint ? (
              <span className="block text-xs text-emerald-700">{email.invoice.hint}</span>
            ) : null}
          </p>
          <div className="mt-2 flex items-center gap-2">
            {ingest.status === 'done' ? (
              <p className="text-xs text-emerald-800">
                ✓ {ingest.fileName} pregătită pentru ingestie. Deschide{' '}
                <a className="underline" href="/chat">
                  AI Chat
                </a>{' '}
                pentru a finaliza.
              </p>
            ) : (
              <button
                type="button"
                onClick={handleIngest}
                disabled={ingest.status === 'pending' || !email.invoice.attachment}
                className="text-fixed-white rounded bg-emerald-600 px-3 py-1.5 text-xs font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                {ingest.status === 'pending' ? 'Se adaugă…' : 'Adaugă în ingestie'}
              </button>
            )}
            {ingest.status === 'error' && (
              <span className="text-xs text-red-600">{ingest.message}</span>
            )}
            {!email.invoice.attachment && (
              <span className="text-xs text-emerald-700">Fără PDF atașat.</span>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

/**
 * Daily Email Summary section (CLE-233). Renders the AI-generated day-level
 * insights and the per-email list (sorted by urgency then recency) with the
 * invoice-ingest action. Only the configured owner mailbox returns emails;
 * everyone else sees a short non-owner notice.
 */
export function EmailSummarySection({ summary, userId }: Props) {
  if (!summary.isOwner) {
    return (
      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="mb-1 font-semibold text-gray-800">Rezumat email</h2>
        <p className="text-sm text-gray-500">
          Rezumatul zilnic de email este disponibil doar pentru căsuța proprietarului conectat.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-semibold text-gray-800">Rezumat email</h2>
        <span className="text-xs text-gray-500">
          {summary.total} {summary.total === 1 ? 'email' : 'email-uri'}
          {summary.invoiceCount > 0 ? ` · ${summary.invoiceCount} facturi` : ''}
        </span>
      </div>

      {!summary.connected && (
        <p className="mb-3 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700">
          Gmail nu este conectat — se afișează doar rezultatele din cache.
        </p>
      )}

      {summary.insights && (
        <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 p-3">
          {summary.insights.executiveSummary && (
            <p className="text-sm text-gray-700">{summary.insights.executiveSummary}</p>
          )}
          {summary.insights.alerts.length > 0 && (
            <ul className="mt-2 space-y-1">
              {summary.insights.alerts.map((alert, i) => (
                <li key={i} className="text-xs text-red-700">
                  ⚠ {alert}
                </li>
              ))}
            </ul>
          )}
          {summary.insights.suggestedActions.length > 0 && (
            <ul className="mt-2 list-inside list-disc space-y-0.5">
              {summary.insights.suggestedActions.map((action, i) => (
                <li key={i} className="text-xs text-gray-600">
                  {action}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {summary.emails.length === 0 ? (
        <p className="text-sm text-gray-500">Niciun email pentru această zi.</p>
      ) : (
        <ul className="space-y-2">
          {summary.emails.map((email) => (
            <EmailRow key={email.id} email={email} userId={userId} />
          ))}
        </ul>
      )}
    </section>
  );
}
